import React, { useState } from 'react';
import { Box, Divider, IconButton, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import './App.css';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import Population from './components/Population';
import useAuth from './hooks/useAuth';
import { useSearchParams } from 'react-router-dom';
import { REGISTER_ID_PARAM_NAME } from './config/constants';
import useModal from './hooks/useModal';
import UserModal from './components/modals/UserModal';
import { createClientProvider } from './contexts/ClientContexts';
import useUserInfo from './hooks/useUserInfo';
import { Logout, Settings } from '@mui/icons-material';
import TabPanel from './components/TabPanel';
import { a11yProps } from './utils/a11y';
import useUsage from './hooks/useUsage';
import { addBackendPrefix } from './utils/backendPrefix';

const {
  ClientContext: ApiContext,
  ClientProvider: ApiProvider,
} = createClientProvider(addBackendPrefix('/api'));
export { ApiContext };

export default function App() {
  const { isLoggedIn, logout } = useAuth();
  const { userInfo, updateUserInfo } = useUserInfo();
  const [searchParams] = useSearchParams();
  const [justRegisteredUsername, setJustRegisteredUsername] = useState<string>();
  const [currentTabIndex, setCurrentTabIndex] = useState(searchParams.get(REGISTER_ID_PARAM_NAME) ? 1 : 0);

  const { modalOpen, showModal, closeModal } = useModal();
  const { usage, refreshUsage } = useUsage();
  
  const handleTabChange = (_event: React.SyntheticEvent, newIndex: number) => {
    setCurrentTabIndex(newIndex);
  };

  const quotaHelp = usage && (usage.quota ?
		    <Typography>
		      You have used ${usage.usage.toFixed(2)} of your ${usage.quota.toFixed(2)} free image-generating credit. To make more images after your credit is exhausted, get your own OpenAI API key and upload it in the user settings under the 'API Key' tab.
		    </Typography>
		  :
		    <Typography>
		      You have used ${usage.usage.toFixed(2)} for image-generation with the API key you have provided. This is approximate and does not include taxes.
		    </Typography>
  );
  
  return (
    <div className="App">
      <header className="App-header">
        uncoolbob / unprompted
      </header>
      <nav>
        {
          isLoggedIn ?
            <div style={{ textAlign: 'center', marginBottom: '10px' }}>
              <Typography variant="body1" display="inline" sx={{ position: 'relative', top: '2px', mx: 1 }}>
                Logged in as <strong>{userInfo?.nickname}</strong>
              </Typography>
	      {usage &&
	       <Tooltip title={quotaHelp}>
		 <Typography
		   variant="body1"
		   display="inline"
		   sx={{
		     position: 'relative',
		     top: '2px',
		     mx: 1,
		     color: usage.quota && usage.usage/usage.quota > 0.8 ? 'red' : undefined,
		   }}
		 >
		   ${usage.usage.toFixed(2)} / {usage.quota ? `\$${usage.quota.toFixed(2)}` : 'unlimited'}
		 </Typography>
	       </Tooltip>
	      }
              <IconButton title="Manage account" onClick={showModal} sx={{ marginLeft: '8px' }}>
                <Settings />
              </IconButton>
              <IconButton title="Log out" onClick={logout} sx={{ marginLeft: '0px' }}>
                <Logout />
              </IconButton>
              <Divider />
            </div>
            : (
              <Box sx={{ width: '100%' }}>
                <Tabs
                  value={currentTabIndex}
                  onChange={handleTabChange}
                  aria-label="auth tabs"
                  centered
                >
                  <Tab label="Log in" {...a11yProps(0)} />
                  <Tab label="Register" {...a11yProps(1)} />
                </Tabs>
                <TabPanel value={currentTabIndex} index={0}>
                  <Login defaultUsername={justRegisteredUsername} />
                  {justRegisteredUsername &&
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <span className="registration-successful">
                        Registration successful!<br />
                        Please now log in as '{justRegisteredUsername}'
                      </span>
                    </div>
                  }
                </TabPanel>
                <TabPanel value={currentTabIndex} index={1}>
                  <Register onSuccess={(name) => {
                    setJustRegisteredUsername(name);
                    setCurrentTabIndex(0);
                  }} />
                </TabPanel>
              </Box>
            )
        }
      </nav >
      <section>
        {
          isLoggedIn &&
          <ApiProvider>
            <Population
	      disableKeyboardShortcuts={modalOpen}
	      openUserModal={showModal}
	      refreshUsage={refreshUsage}
	    />
          </ApiProvider>
        }
      </section>
      {isLoggedIn && <Divider />}
      <UserModal userInfo={userInfo} updateUserInfo={updateUserInfo} open={modalOpen} onClose={closeModal} />
    </div >
  );
}
